import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import i18nInstance, { changeI18nLanguage } from '@/i18n';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';

/**
 * W22-F1-S3 — read-on-boot side of the cross-device locale sync.
 *
 * On first mount after authentication, fetches the Core profile via the
 * Senviro `/api/v1/proxy/core/users/me` proxy and seeds the active i18next
 * language from `preferences.language` (or `extra_data.preferences.language`,
 * which is where Core surfaces user-scoped preference bags today).
 *
 * Failure modes are all silent — if Core is unreachable, returns 5xx, or
 * does not carry a recognisable `preferences.language`, the existing
 * localStorage-detected language stands. No toast fires.
 *
 * **Write side is intentionally absent.** As of 2026-05-08, Core's
 * `UserUpdate` schema (see `packages/core-api-client/src/generated/types.ts`,
 * line ~26240) exposes no `preferences` field, and `/api/v1/users/me` has no
 * PATCH/PUT operation at all. The Senviro satellite `user-preferences`
 * endpoint (`apps/api/src/user-preferences/`) remains the persistence target
 * for the `language` preference; Core simply seeds the initial value when it
 * already happens to carry one.
 */

interface CoreUserBootstrap {
  email?: string;
  preferences?: { language?: string } | Record<string, unknown>;
  extra_data?: {
    preferences?: { language?: string } | Record<string, unknown>;
  } & Record<string, unknown>;
}

export function extractCoreLanguage(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as CoreUserBootstrap;
  const fromTop =
    p.preferences && typeof p.preferences === 'object'
      ? (p.preferences as Record<string, unknown>).language
      : undefined;
  if (typeof fromTop === 'string' && fromTop.length > 0) return fromTop;
  const fromExtra =
    p.extra_data &&
    typeof p.extra_data === 'object' &&
    p.extra_data.preferences &&
    typeof p.extra_data.preferences === 'object'
      ? (p.extra_data.preferences as Record<string, unknown>).language
      : undefined;
  if (typeof fromExtra === 'string' && fromExtra.length > 0) return fromExtra;
  return null;
}

/**
 * Fire-and-forget hook: must NOT block render. Suspense / loading states are
 * intentionally avoided — the UI shows whatever localStorage said until Core
 * tells us otherwise.
 *
 * 2026-05-15 — the hook now only seeds from Core when localStorage does NOT
 * already carry an explicit user choice. Previously the Core read would
 * override the user's locally-saved language on every login (Core's
 * `preferences.language` had no FE-write path — see this file's header),
 * so users who switched to English in Settings were silently reverted to
 * Norwegian (or whatever Core had) on next boot.
 *
 * The intent of cross-device seeding is preserved: a brand-new device with
 * no localStorage will still pick up the user's stored Core preference.
 * Existing devices with an explicit FE choice are now sticky.
 */
const LOCAL_STORAGE_KEY = 'senviro.locale';

function hasExplicitLocalChoice(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const v = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    return typeof v === 'string' && v.length > 0;
  } catch {
    return false;
  }
}

export function useCoreLanguageSync(): void {
  const { isAuthenticated, getAccessToken } = useAuth();
  const appliedRef = useRef(false);

  const query = useQuery({
    queryKey: ['core', 'users', 'me', 'language-bootstrap'],
    queryFn: () =>
      apiFetch<unknown>('/api/v1/proxy/core/users/me', { getAccessToken }),
    // Only run the lookup when the user has no explicit local choice yet.
    // Existing choice is sticky — Core can't override it.
    enabled: isAuthenticated && !hasExplicitLocalChoice(),
    staleTime: 5 * 60_000,
    retry: 0,
    // Swallow errors — Core unreachable is non-fatal here.
    throwOnError: false,
  });

  useEffect(() => {
    if (appliedRef.current) return;
    // Belt-and-braces: even if the query somehow ran (cache hit from a
    // prior session), bail out the moment we see a local choice.
    if (hasExplicitLocalChoice()) {
      appliedRef.current = true;
      return;
    }
    if (!query.data) return;
    const next = extractCoreLanguage(query.data);
    if (!next) return;
    if (i18nInstance.language === next) {
      appliedRef.current = true;
      return;
    }
    appliedRef.current = true;
    void changeI18nLanguage(next);
  }, [query.data]);
}
